
import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DownstreamSheetsFilteredByFieldQuery,
  DownstreamSheetsFilteredByFieldQueryVariables,
} from '../../../gen-src/queries/DownstreamSheetsFilteredByFieldQuery';
import { SheetsDisplayGridRowDataFragment } from '../../../gen-src/queries/SheetsDisplayGridRowDataFragment';
import {
  WorkbookDownstreamSheetsQuery,
  WorkbookDownstreamSheetsQueryVariables,
} from '../../../gen-src/queries/WorkbookDownstreamSheetsQuery';
import { SheetSortOrder } from '../../../gen-src/queries/globalTypes';
import { DownstreamSheetsFilteredByFieldQueryGql } from '../../queries/DownstreamSheetsFilteredByFieldQueryGql';
import { WorkbookDownstreamSheetsQueryGql } from '../../queries/WorkbookDownstreamSheetsQueryGql';
import { getRowDataForSheet, SheetsDisplayGrid, SheetsDisplayGridRowData } from '../sharedDisplayGrid/SheetsDisplayGrid';
import { SheetsValidSortFields } from '../sorting/sortProviders/SheetsGridSortProvider';

type QueryVariables = WorkbookDownstreamSheetsQueryVariables;
type QueryResults = WorkbookDownstreamSheetsQuery;
type QueryResultListNode = SheetsDisplayGridRowDataFragment;
type GridRowData = SheetsDisplayGridRowData;

type QueryVariablesWithFilter = DownstreamSheetsFilteredByFieldQueryVariables;
type QueryResultsWithFilter = DownstreamSheetsFilteredByFieldQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.workbooks && resultData.workbooks[0] && resultData.workbooks[0].sheetsConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && resultData.fieldsConnection.nodes[0].downstreamSheetsConnection;
}

export type WorkbookDownstreamSheetsQueryGridProps =
  GridSortProps<SheetsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredFieldId: string | null;
    readonly rowCount: number | null;
  };

export function WorkbookDownstreamSheetsQueryGrid(props: WorkbookDownstreamSheetsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamSheetsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredSheetsLineageGridMessage();

  return props.filteredFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={DownstreamSheetsFilteredByFieldQueryGql}
      partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as SheetSortOrder }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForSheet}
    >
      {({ rowStore }) => <SheetsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={WorkbookDownstreamSheetsQueryGql}
        partialQueryVariables={{ workbookVizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as SheetSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForSheet}
      >
        {({ rowStore }) => <SheetsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    );
}
